/**
 * Kit UI partagé des onglets de la fiche agence (console super-admin).
 * Objectif : un langage visuel homogène et moderne sur tous les onglets.
 */
import type { ReactNode } from "react";

/* --------------------------------- Panel ---------------------------------- */
export function Panel({
  children,
  className = "",
  padded = true,
  accent,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
  /** Liseré gauche d'accent : info, success, warning, danger. */
  accent?: "info" | "success" | "warning" | "danger";
}) {
  const ACCENT: Record<string, string> = {
    info: "border-l-4 border-l-accent-500",
    success: "border-l-4 border-l-emerald-500",
    warning: "border-l-4 border-l-amber-500",
    danger: "border-l-4 border-l-red-500",
  };
  return (
    <div
      className={`rounded-2xl bg-white ring-1 ring-navy-100 shadow-sm ${
        padded ? "p-5" : ""
      } ${accent ? ACCENT[accent] : ""} ${className}`.trim()}
    >
      {children}
    </div>
  );
}

/* ------------------------------ Section header ---------------------------- */
export function SectionTitle({
  children,
  action,
  desc,
}: {
  children: ReactNode;
  /** Conservé pour compat — non rendu (design sobre, sans icônes décoratives). */
  icon?: ReactNode;
  action?: ReactNode;
  desc?: ReactNode;
}) {
  return (
    <div className="mb-4 flex items-start justify-between gap-3">
      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-navy-500">
          {children}
        </h3>
        {desc ? <p className="mt-1 text-sm text-navy-500">{desc}</p> : null}
      </div>
      {action ?? null}
    </div>
  );
}

/* -------------------------------- Stat tile ------------------------------- */
const STAT_TONES = {
  navy: "bg-navy-50 text-navy-700",
  accent: "bg-accent-50 text-accent-600",
  emerald: "bg-emerald-50 text-emerald-600",
  amber: "bg-amber-50 text-amber-600",
  slate: "bg-navy-100 text-navy-500",
} as const;

export function StatTile({
  label,
  value,
  caption,
}: {
  /** Conservé pour compat — non rendu (design sobre, sans icônes). */
  icon?: ReactNode;
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  tone?: keyof typeof STAT_TONES;
}) {
  return (
    <div className="rounded-2xl bg-white p-5 ring-1 ring-navy-100 shadow-sm">
      <p className="text-xs font-semibold uppercase tracking-wider text-navy-500">
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-bold tracking-tight text-navy-800">
        {value}
      </p>
      {caption ? <p className="mt-0.5 text-xs text-navy-400">{caption}</p> : null}
    </div>
  );
}

/* ------------------------------- Key / value ------------------------------ */
export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5">
      <span className="text-sm text-navy-500">{label}</span>
      <span className="text-right text-sm font-semibold text-navy-800">
        {children}
      </span>
    </div>
  );
}

/* ---------------------------------- Badge --------------------------------- */
const BADGE_TONES = {
  navy: "bg-navy-100 text-navy-600 ring-navy-200",
  accent: "bg-accent-50 text-accent-700 ring-accent-100",
  emerald: "bg-emerald-50 text-emerald-700 ring-emerald-100",
  amber: "bg-amber-50 text-amber-800 ring-amber-100",
  red: "bg-red-50 text-red-700 ring-red-100",
  slate: "bg-navy-100 text-navy-500 ring-navy-200",
} as const;

export function Badge({
  children,
  tone = "navy",
  dot = false,
}: {
  children: ReactNode;
  tone?: keyof typeof BADGE_TONES;
  dot?: boolean;
}) {
  const DOT: Record<string, string> = {
    navy: "bg-navy-400",
    accent: "bg-accent-500",
    emerald: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    slate: "bg-navy-300",
  };
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${BADGE_TONES[tone]}`}
    >
      {dot ? <span className={`h-1.5 w-1.5 rounded-full ${DOT[tone]}`} /> : null}
      {children}
    </span>
  );
}

/* ------------------------------- Empty inline ----------------------------- */
export function EmptyInline({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-xl bg-navy-50/60 px-5 py-8 text-center text-sm text-navy-400 ring-1 ring-navy-100">
      {children}
    </div>
  );
}

/* -------------------------------- Helpers --------------------------------- */
export function fmtMoney(n: number): string {
  return n.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
export function fmtDate(d: string | Date): string {
  return new Date(d).toLocaleDateString("fr-FR");
}
export function fmtDateTime(d: string | Date): string {
  return new Date(d).toLocaleString("fr-FR", {
    dateStyle: "short",
    timeStyle: "short",
  });
}
export function initials(name: string): string {
  const parts = name.replace(/[[\]]/g, "").trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? "?";
  const b = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (a + b).toUpperCase();
}
